/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-prototype-builtins */
import { Grid, Row, Table as AntdTable } from 'antd'
import { SorterResult, TableLocale, TablePaginationConfig } from 'antd/lib/table/interface'
import KSearch from 'app/components/Modules/KSearch'
import Empty from 'app/components/UI-Elements/Feedback/Empty'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { TableProps } from 'app/constants/GlobalTypes'
import { GenericObject } from 'app/constants/GlobalTypes'
import { ToolBox } from 'app/lib/hooks/toolBox'
import useFilterParams from 'app/lib/hooks/useFilterParams'
import { useIndex, UseIndexOptions } from 'app/lib/hooks/useQuery'
import { RecordTypeEnum } from 'app/queries'
import { ComponentProps, ReactText, useState } from 'react'

import { SpinTip } from '../General/KSpin'
import KTableRow from './KTableRow'

interface Props<T> extends TableProps<T & any> {
  recordType: RecordTypeEnum
  queryOptions?: Partial<UseIndexOptions<T>>
  searchProps?: ComponentProps<typeof KSearch>
  transformData?: (data: Array<T>) => Array<T>
}

function KTable<T>({
  size = 'middle',
  showSearch = false,
  onRowClick,
  onRowHover,
  recordType,
  params,
  locale,
  filters,
  filtersExtra,
  tableExtra,
  empty,
  dataSource,
  renderMobileRow,
  pageSize = 10,
  queryOptions,
  searchProps,
  loading: loadingProp,
  transformData = (data) => data,
  expandable = {},
  ...others
}: Props<T>) {
  const {
    params: searchParams,
    searchParams: urlSearchParams,
    setSearchParams
  } = useFilterParams<GenericObject<string | number | boolean>>()

  const {
    data: response,
    loading,
    onPageChange,
    pageSize: currentPageSize
  } = useIndex<T>(recordType, {
    paginationWithSearchParams: true,
    pageSize,
    variables: { ...params, ...searchParams, query: searchParams.query?.toString() },
    skip: Boolean(dataSource), // do not execute query
    ...queryOptions
  })
  const [searchText, setSearchText] = useState<string>(urlSearchParams.get('query'))
  const { md } = Grid.useBreakpoint()
  const { tableRow, generateId } = ToolBox.useId()
  const data = transformData(dataSource || response?.nodes || [])
  const hasFixedColumns = others.columns?.some((column) => column.fixed)
  const { defaultExpandAllRows } = expandable

  const getRowClassName = (): string => (onRowClick ? 'pointer' : '')

  const onSearch = (value: string) => {
    setSearchText(value)
    setSearchParams({ ...searchParams, query: value })
  }

  const getLocale = (): TableLocale => {
    if (!empty) return locale

    if (searchText && empty.emptySearchText) {
      return {
        ...locale,
        emptyText: <Empty description={empty.emptySearchText} />
      }
    }

    if (empty.default) {
      return {
        ...locale,
        emptyText: empty.default
      }
    }
  }

  const getSortString = (sorter: SorterResult<T>): string => {
    if (
      sorter.hasOwnProperty('order') &&
      (sorter.column?.hasOwnProperty('dataIndex') || sorter.column?.hasOwnProperty('key'))
    ) {
      return `${sorter.order === 'ascend' ? '' : '-'}${
        sorter.column?.key || sorter.column?.dataIndex
      }`
    } else {
      return ''
    }
  }

  const getPaginationConfig = (): TablePaginationConfig => {
    return {
      total: response.totalCount,
      pageSize: currentPageSize,
      hideOnSinglePage: true,
      current: response.currentPage,
      showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} von ${total} Ergebnissen`
    }
  }

  const getRow = (props: { record: T }) => {
    const { record, ...others } = props
    if (!record) return null
    return <KTableRow renderMobileRow={renderMobileRow} record={record} {...others} />
  }

  const onChange = async (
    p: TablePaginationConfig,
    _f: Record<string, ReactText[]>,
    _sorter: SorterResult<T>
  ) => {
    const sort = getSortString(_sorter)
    console.log({ sort, description: 'sorting will be implemented' })

    onPageChange(p.current, p.pageSize)
  }

  // force re-render after data loading, only when defaultExpandAllRows is true
  const key = JSON.stringify({
    id: generateId({ recordType, id: 'index', componentType: 'table' }),
    defaultExpandAllRows: defaultExpandAllRows && loading
  })

  return (
    <Row align={'middle'} gutter={[12, { xs: 24, sm: 24, md: 24, lg: 12 }]}>
      <KCol>{filters}</KCol>
      <KCol render={showSearch}>
        <KSearch
          defaultValue={urlSearchParams.get('query')}
          onSearch={onSearch}
          style={{ width: 300 }}
          recordType={recordType}
          enterButton
          {...searchProps}
        />
      </KCol>
      <If condition={filtersExtra}>
        <KCol>{filtersExtra}</KCol>
      </If>
      <KCol flex="auto"></KCol>
      <If condition={md}>
        <KCol>{tableExtra}</KCol>
      </If>
      <KCol span={24}>
        <AntdTable
          rowKey={'id'}
          key={key}
          size={size}
          scroll={hasFixedColumns ? { x: '100%' } : {}}
          loading={{ spinning: loadingProp || loading, tip: SpinTip }}
          dataSource={data}
          locale={getLocale()}
          onChange={onChange}
          pagination={getPaginationConfig()}
          rowClassName={`fade-in-slow ${getRowClassName()}`}
          components={data.length && renderMobileRow && { body: { row: getRow } }}
          expandable={expandable}
          onRow={(record, index) => {
            return {
              id: tableRow({ recordType, id: record.id }),
              onClick: (e) => onRowClick && onRowClick(record, index, e),
              onMouseEnter: (e) => onRowHover && onRowHover(record, index, e),
              record
            }
          }}
          {...others}
        />
      </KCol>
    </Row>
  )
}

export default KTable
